/**
 * Drag-and-drop + file-picker upload control for a KB workspace. Accepts
 * `.txt,.md,.pdf`, uploads multiple files concurrently, and tracks per-file
 * state (`uploading` → `done`/`error`). The interaction model mirrors the
 * canonical `plugins/uploads/client/pages/uploads.page.tsx`; the actual POST
 * goes through `useUploadDocument`, which invalidates the document list (so a
 * fresh `pending` row appears and live-status polling kicks in).
 *
 * Done rows self-dismiss shortly after success — the uploaded document then
 * shows up in the live document list below with its own status chip.
 */
import { Button } from "@d12labs/ui/button";
import { UploadCloud } from "lucide-react";
import { useRef, useState } from "react";
import { useUploadDocument } from "../hooks/use-kb";
import { ACCEPTED_FILE_EXTENSIONS, formatBytes } from "../types";

type PendingUpload = {
	localId: string;
	name: string;
	size: number;
	status: "uploading" | "done" | "error";
	note?: string;
};

/** How long a completed row lingers before it auto-dismisses (ms). */
const DONE_LINGER_MS = 2500;

export function UploadDropzone({
	workspaceId,
	compact = false,
}: {
	workspaceId: string;
	/** Compact vertical dropzone (sits beside the stat strip on the Documents tab). */
	compact?: boolean;
}) {
	const upload = useUploadDocument(workspaceId);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [pending, setPending] = useState<PendingUpload[]>([]);
	const [dragging, setDragging] = useState(false);

	const startUploads = (fileList: FileList | File[]) => {
		const list = Array.from(fileList);
		if (list.length === 0) return;

		const entries: PendingUpload[] = list.map((f) => ({
			localId: crypto.randomUUID(),
			name: f.name,
			size: f.size,
			status: "uploading",
		}));
		setPending((prev) => [...prev, ...entries]);

		entries.forEach((entry, i) => {
			const file = list[i];
			if (!file) return;
			upload
				.mutateAsync(file)
				.then((result) => {
					setPending((prev) =>
						prev.map((p) =>
							p.localId === entry.localId
								? { ...p, status: "done", note: result.deduped ? "Already indexed" : "Uploaded" }
								: p,
						),
					);
					// Let the finished row linger briefly, then drop it — the document
					// now lives in the list below with its real ingestion status.
					setTimeout(() => {
						setPending((prev) => prev.filter((p) => p.localId !== entry.localId));
					}, DONE_LINGER_MS);
				})
				.catch((err: Error) => {
					setPending((prev) =>
						prev.map((p) =>
							p.localId === entry.localId ? { ...p, status: "error", note: err.message } : p,
						),
					);
				});
		});
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) startUploads(files);
		// Reset so re-picking the same file fires change again.
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setDragging(false);
		const files = e.dataTransfer.files;
		if (files.length > 0) startUploads(files);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (!dragging) setDragging(true);
	};

	const dismissPending = (localId: string) => {
		setPending((prev) => prev.filter((p) => p.localId !== localId));
	};

	return (
		<div className={compact ? "flex h-full min-h-[132px] flex-col gap-3" : "space-y-3"}>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				accept={ACCEPTED_FILE_EXTENSIONS}
				onChange={handleFileChange}
				className="hidden"
			/>

			{compact ? (
				<button
					type="button"
					onClick={() => fileInputRef.current?.click()}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={() => setDragging(false)}
					className={[
						"group flex w-full flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed p-4 text-center transition-colors cursor-pointer",
						dragging
							? "border-primary bg-primary/5"
							: "border-hairline-strong bg-surface-1/40 hover:border-primary/50 hover:bg-surface-1",
					].join(" ")}
				>
					<span className="flex size-8 items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-muted group-hover:text-primary">
						<UploadCloud className="size-4" />
					</span>
					<span className="text-body-sm font-medium text-foreground">Drop documents to index</span>
					<span className="text-meta text-ink-subtle">Up to 25 MB · TXT, Markdown, PDF</span>
				</button>
			) : (
				<button
					type="button"
					onClick={() => fileInputRef.current?.click()}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={() => setDragging(false)}
					className={[
						"flex w-full items-center gap-3 rounded-lg border border-dashed p-5 text-left transition-colors cursor-pointer",
						dragging
							? "border-primary bg-primary/5 text-foreground"
							: "border-hairline-strong bg-surface-1/40 hover:bg-surface-1 hover:border-hairline",
					].join(" ")}
				>
					<span className="flex size-9 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-muted">
						<UploadCloud className="size-4" />
					</span>
					<div className="flex-1">
						<div className="text-body-sm font-medium text-foreground">Drop documents to index</div>
						<div className="text-meta text-ink-subtle">Up to 25 MB each · TXT, Markdown, PDF</div>
					</div>
					<Button
						type="button"
						variant="secondary"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							fileInputRef.current?.click();
						}}
					>
						Choose files
					</Button>
				</button>
			)}

			{pending.length > 0 ? (
				<ul className="flex flex-col gap-1.5">
					{pending.map((p) => (
						<li
							key={p.localId}
							className="flex items-center gap-2 rounded-md border border-hairline bg-surface-1 px-3 py-2"
						>
							<div className="min-w-0 flex-1">
								<div className="truncate text-body-sm text-foreground">{p.name}</div>
								<div className="text-meta text-ink-subtle">{formatBytes(p.size)}</div>
							</div>
							{p.status === "uploading" ? (
								<span className="text-meta text-ink-subtle">Uploading…</span>
							) : p.status === "done" ? (
								<span className="text-meta text-semantic-success">{p.note ?? "Uploaded"}</span>
							) : (
								<>
									<span className="truncate text-meta text-destructive">{p.note ?? "Failed"}</span>
									<Button
										type="button"
										variant="ghost"
										size="icon-sm"
										onClick={() => dismissPending(p.localId)}
										aria-label="Dismiss"
									>
										<span className="text-meta">✕</span>
									</Button>
								</>
							)}
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}
